import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Switch } from '@sorare/core/src/atoms/inputs/Switch';
import { TextInput } from '@sorare/core/src/atoms/inputs/TextInput';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ANY_SPORT_MARKET_WATCHLISTS_ID } from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { glossary } from '@sorare/core/src/lib/glossary';
import { thickMaterial } from '@sorare/core/src/style/utils';

import { useWatchlists } from '#src/hooks/watchlist/useWatchlists.tsx';

import type { UpsertWatchlistDialog_watchlist } from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
`;

const DrawerWrapper = styled(Wrapper)`
  ${thickMaterial}
  border-top-left-radius: var(--double-unit);
  border-top-right-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: ({ id, title }: { id: string; title: string }) => void;
  watchlist?: UpsertWatchlistDialog_watchlist;
};

export const UpsertWatchlistDialog = ({
  open,
  onClose,
  onSuccess,
  watchlist,
}: Props) => {
  const { isIosApp } = useIsMobileApp();
  const { showNotification } = useSnackNotificationContext();
  const { generateSportContextPath } = useSportContext();

  const watchlistId = watchlist?.id;
  const [inputTitle, setInputTitle] = useState<string | undefined>(
    watchlist?.title
  );
  const [inputPublic, setInputPublic] = useState<boolean>(
    watchlist?.public ?? false
  );

  const { upsertWatchlist } = useWatchlists();
  const { up: tabletOrAbove } = useScreenSize('tablet');
  const { formatMessage } = useIntlContext();

  const onSubmit = async () => {
    if (!inputTitle) {
      return;
    }
    const res = await upsertWatchlist.mutate(
      {
        title: inputTitle,
        watchlistId,
      },
      inputPublic
    );
    if (!res.errors.length) {
      const w = res?.data?.upsertWatchlist?.watchlist;
      if (w) {
        onSuccess?.(w);
        if (watchlistId) {
          showNotification('watchlistUpdated', {
            title: w.title,
          });
        } else {
          showNotification('watchlistCreated', {
            title: w.title,
            link: (chunks: string) =>
              w.id && !isIosApp ? (
                <StyledLink
                  to={generateSportContextPath(ANY_SPORT_MARKET_WATCHLISTS_ID, {
                    params: {
                      id: idFromObject(w.id),
                    },
                  })}
                >
                  {chunks}
                </StyledLink>
              ) : (
                chunks
              ),
          });
        }
        if (!watchlist) {
          setInputTitle(undefined);
          setInputPublic(false);
        }
      }
      onClose();
    }
  };

  const titleNode = (
    <HeadlineL bold className="text-center">
      {watchlist ? (
        <FormattedMessage
          defaultMessage="Edit watchlist “{title}”"
          values={{ title: watchlist.title }}
        />
      ) : (
        <FormattedMessage defaultMessage="New Watchlist" />
      )}
    </HeadlineL>
  );
  const content = (
    <>
      <Vertical gap={0.5}>
        <BodyM color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="Name" />
        </BodyM>
        <TextInput
          placeholder={formatMessage({
            defaultMessage: 'E.g.: “U23 Rising stars”',
          })}
          value={inputTitle}
          onChange={e => setInputTitle(e.target.value)}
        />
      </Vertical>
      <SBHorizontal gap={0}>
        <BodyM color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="Public" />
        </BodyM>
        <Switch
          checked={inputPublic}
          onChange={e => setInputPublic(e.target.checked)}
        />
      </SBHorizontal>
    </>
  );
  const actions = (
    <LoadingButton
      loading={upsertWatchlist.loading}
      fullWidth
      size="medium"
      color="primary"
      disabled={!inputTitle}
      onClick={onSubmit}
    >
      {watchlist ? (
        <FormattedMessage {...glossary.confirm} />
      ) : (
        <FormattedMessage defaultMessage="Create watchlist" />
      )}
    </LoadingButton>
  );
  if (tabletOrAbove) {
    return (
      <Dialog
        maxWidth="xs"
        fullWidth
        open={open}
        onClose={onClose}
        title={titleNode}
        footer={actions}
      >
        <Wrapper>{content}</Wrapper>
      </Dialog>
    );
  }
  return (
    <Drawer side="bottom" blur open={open} onBackdropClick={onClose}>
      <DrawerWrapper gap={2}>
        <SBHorizontal>
          {titleNode}
          <CloseButton size="medium" onClose={onClose} />
        </SBHorizontal>

        <Vertical gap={0.5}>{content}</Vertical>
        {actions}
      </DrawerWrapper>
    </Drawer>
  );
};

UpsertWatchlistDialog.fragments = {
  watchlist: gql`
    fragment UpsertWatchlistDialog_watchlist on Watchlist {
      slug
      id
      title
      public
    }
  ` as TypedDocumentNode<UpsertWatchlistDialog_watchlist>,
};
